// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import './MaterialDropdown.scss';
import { useTranslation } from 'react-i18next';
import { isMobileOnly } from 'react-device-detect';

import PropertyField from '../PropertyField/PropertyField';

import material_icon from '../../assets/icons/white/piechart.png';
import { useAuthStore } from '../../stores/authStore';
import {
  getLabelDisplayName,
  getLabelsetBelongingToGroupByLabel,
} from '../../shared_components/utils/FirebaseLabelsetsUtils';

export default function MaterialDropdown({ value, handleChange, splitProductGroup = true, editable = true }) {
  const { i18n, t } = useTranslation();
  const defaultLabelsetGroups = useAuthStore((state) => state.defaultLabelsetGroups);
  const globalLabelsets = useAuthStore((state) => state.globalLabelsets);

  function handleDeliveryMaterialChange(e) {
    const labelId = e.target.value;
    const labelsetId = getLabelsetBelongingToGroupByLabel(labelId, globalLabelsets, 'materials');
    if (!labelsetId) return;

    handleChange({ ...e, target: { name: 'delivery_labels', value: { [labelsetId]: labelId } } });
  }

  if (splitProductGroup) {
    /* Split product group and material into two dropdowns */
    const productGroupOptions = {};
    if (defaultLabelsetGroups?.materials?.labels?.[value]?.product_group) {
      productGroupOptions[value] = defaultLabelsetGroups?.materials?.labels?.[value]?.product_group;
    }
    for (const [id, material] of Object.entries(defaultLabelsetGroups?.materials?.labels)) {
      if (Object.values(productGroupOptions).includes(material.product_group)) continue;
      productGroupOptions[id] = material.product_group;
    }

    const materialOptions = {};
    for (const [id, material] of Object.entries(defaultLabelsetGroups?.materials?.labels)) {
      if (material.product_group == defaultLabelsetGroups?.materials?.labels?.[value]?.product_group) {
        materialOptions[id] = getLabelDisplayName(defaultLabelsetGroups?.materials, id, i18n.language);
      }
    }

    let grid_settings = { gridColumn: 'span 1', gridTemplateColumns: '1fr' };
    if (Object.keys(productGroupOptions).length > 1) {
      if (isMobileOnly) {
        grid_settings = {
          gridColumn: 'span 1',
          gridTemplateColumns: '1fr',
          gridRow: 'span 2',
          gridTemplateRows: '1fr 1fr',
        };
      } else {
        grid_settings = { gridColumn: 'span 2', gridTemplateColumns: '1fr 1fr' };
      }
    }

    return (
      <div className='material-selector' style={grid_settings}>
        {Object.keys(productGroupOptions).length > 1 && (
          <PropertyField
            icon={material_icon}
            title={t('property_material_group')}
            value={value}
            name='materials'
            type='select'
            handleChange={handleDeliveryMaterialChange}
            editable={editable}
            options={productGroupOptions}
          />
        )}
        <PropertyField
          icon={material_icon}
          title={t('property_material')}
          value={value}
          name='materials'
          type='select'
          handleChange={handleDeliveryMaterialChange}
          editable={editable}
          options={materialOptions}
        />
      </div>
    );
  } else {
    /* Only show a single dropdown, mixing all product-groups together */
    const materialOptions = Object.keys(defaultLabelsetGroups?.materials?.labels || {}).reduce((acc, id) => {
      acc[id] = getLabelDisplayName(defaultLabelsetGroups?.materials, id, i18n.language);
      return acc;
    }, {});

    return (
      <PropertyField
        icon={material_icon}
        title={t('property_material')}
        value={value}
        name='materials'
        type='select'
        options={materialOptions}
        handleChange={handleDeliveryMaterialChange}
        editable={editable}
      />
    );
  }
}
